import { useEffect, useState } from 'react';
import { Award, CheckCircle2, GraduationCap } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { issueCertificate, loadCourseProgressSummaries, loadMyCertificates } from '../lib/certificates';
import type { CourseProgress, MyCertificate } from '../lib/certificates';
import { Badge, Button, EmptyState, LoadingState, ProgressBar, SectionHeading } from '../components/ui';

export function StudentCertificates() {
  const navigate = useNavigate();
  const [certs, setCerts] = useState<MyCertificate[]>();
  const [progress, setProgress] = useState<CourseProgress[]>();
  const [claiming, setClaiming] = useState<string>();
  const [claimError, setClaimError] = useState('');

  useEffect(() => {
    let active = true;
    void Promise.all([
      loadMyCertificates().catch(() => [] as MyCertificate[]),
      loadCourseProgressSummaries().catch(() => [] as CourseProgress[]),
    ]).then(([certificateRows, summaries]) => {
      if (!active) return;
      setCerts(certificateRows);
      setProgress(summaries);
    });
    return () => { active = false; };
  }, []);

  if (!certs || !progress) return <LoadingState />;
  const earnedCourseIds = new Set(certs.map((cert) => String((cert.CoursePtr as { objectId?: string })?.objectId ?? '')));
  const ready = progress.filter((item) => item.complete && !earnedCourseIds.has(item.courseId));
  const learning = progress.filter((item) => !item.complete);

  const claim = async (course: CourseProgress) => {
    setClaiming(course.courseId);
    setClaimError('');
    try {
      const cert = await issueCertificate(course.courseId);
      if (cert?.objectId) navigate(`/certificates/${cert.objectId}`);
      else setClaimError('The certificate could not be created. Please try again.');
    } catch (error) {
      setClaimError(error instanceof Error ? error.message : 'The certificate could not be created. Please try again.');
    } finally { setClaiming(''); }
  };

  return <div className="utility-page">
    <section className="utility-hero"><div className="utility-icon"><Award size={28} /></div><div><h1>Certificates</h1><p>Finish every lecture in a course to earn its certificate</p></div></section>

    <section className="detail-section">
      <SectionHeading title={`Earned · ${certs.length}`} />
      {certs.length ? <div className="subject-list">{certs.map((cert) => (
        <Link className="subject-row" key={cert.objectId} to={`/certificates/${cert.objectId}`}>
          <span className="material-icon"><GraduationCap size={17} /></span>
          <div><strong>{cert.courseTitle}</strong><small>{cert.CertificateId} · issued {formatDate(cert.issuedAt)}</small></div>
          <Badge tone="green"><CheckCircle2 size={12} /> Earned</Badge>
        </Link>
      ))}</div>
        : <p className="detail-description">No certificates yet — complete a course below to earn your first one.</p>}
    </section>

    {ready.length > 0 && <section className="detail-section">
      <SectionHeading title={`Ready to claim · ${ready.length}`} />
      {claimError && <p className="form-error" role="alert">{claimError}</p>}
      <div className="subject-list">{ready.map((item) => (
        <div className="subject-row" key={item.courseId}>
          <span className="material-icon"><Award size={17} /></span>
          <div><strong>{item.title}</strong><small>All {item.totalLectures} lectures completed</small></div>
          <Button variant="soft" disabled={claiming === item.courseId} onClick={() => void claim(item)}>{claiming === item.courseId ? 'Claiming…' : 'Claim'}</Button>
        </div>
      ))}</div>
    </section>}

    <section className="detail-section">
      <SectionHeading title={learning.length ? `In progress · ${learning.length}` : 'In progress'} />
      {learning.length ? <div className="subject-list">{learning.map((item) => (
        <div className="subject-row" key={item.courseId}>
          <span className="material-icon"><CheckCircle2 size={16} /></span>
          <div><strong>{item.title}</strong><small>{item.completedLectures} of {item.totalLectures} lectures finished</small><ProgressBar value={item.percent} completed={item.complete} /></div>
          <strong className="cert-percent">{item.percent}%</strong>
        </div>
      ))}</div>
        : <p className="detail-description">Start any course lecture and your progress will show up here.</p>}
    </section>

    {!certs.length && !learning.length && <EmptyState icon={Award} title="Nothing to certify yet" description="Watch lectures inside a course — your completion percentage builds up here." />}
  </div>;
}

function formatDate(value?: string) {
  return value ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '';
}
